import type { HttpClient } from '@shared/http/http-client';
import type {
  OrderChatMessage,
  OrderChatMessageKind,
  OrderChatOrder,
} from '@domain/models/order-chat.interface';
import type { IOrderChatRepository } from '@domain/data-contracts/order-chat-repository.interface';

interface OrderChatOrderDto {
  readonly orderId: number;
  readonly orderNumber: number;
  readonly patientInitials: string | null;
  readonly status: string;
  readonly dueAt: string | null;
  readonly labName: string | null;
}

interface OrderChatMessageDto {
  readonly messageId: number;
  readonly clientMessageId: string;
  readonly orderId: number;
  readonly senderType: 'Client' | 'Employee' | 'Assistant';
  readonly senderId: number;
  readonly senderName: string;
  readonly kind: OrderChatMessageKind;
  readonly body: string;
  readonly createdAtUtc: string;
  readonly isOwnMessage: boolean;
}

const BASE = '/api/order-chat';

export class OrderChatRepository implements IOrderChatRepository {
  public constructor(private readonly http: HttpClient) {}

  public async listOrders(): Promise<readonly OrderChatOrder[]> {
    const response = await this.http.get<readonly OrderChatOrderDto[]>(`${BASE}/orders`);
    return response.data.map((order) => ({
      ...order,
      orderId: String(order.orderId),
    }));
  }

  public async getMessages(orderId: string): Promise<readonly OrderChatMessage[]> {
    const response = await this.http.get<readonly OrderChatMessageDto[]>(
      `${BASE}/orders/${encodeURIComponent(orderId)}/messages`,
    );
    return response.data.map(mapMessage);
  }

  public async sendMessage(
    orderId: string,
    clientMessageId: string,
    kind: OrderChatMessageKind,
    body: string,
  ): Promise<OrderChatMessage> {
    const response = await this.http.post<OrderChatMessageDto>(
      `${BASE}/orders/${encodeURIComponent(orderId)}/messages`,
      { clientMessageId, kind, body },
    );
    return mapMessage(response.data);
  }
}

function mapMessage(dto: OrderChatMessageDto): OrderChatMessage {
  return {
    id: String(dto.messageId),
    clientMessageId: dto.clientMessageId,
    orderId: String(dto.orderId),
    senderType: dto.senderType,
    senderId: String(dto.senderId),
    senderName: dto.senderName,
    kind: dto.kind,
    body: dto.body,
    createdAtUtc: dto.createdAtUtc,
    isOwnMessage: dto.isOwnMessage,
  };
}
